/**
 * Branching engine — pure logic, no React, no DB.
 *
 * This is the CANONICAL home for the visit form's branching/visibility/render-order
 * rules. The mobile app renders questions with exactly these functions
 * (`apps/mobile/lib/branching.ts` mirrors this), and the server's ground-truth
 * loader uses them so the validation LLM sees questions in the same order and
 * with the same visibility the RN saw on the device.
 *
 * Question ids are strings here (the mobile contract). Server callers that hold
 * numeric ids convert with String(id) at the boundary.
 */
/** Evaluate a single branching rule against current answers. */
function evaluateRule(rule, answers) {
    const answer = answers[rule.sourceQuestionId];
    if (answer === undefined || answer === null) {
        return false;
    }
    switch (rule.logicOperator) {
        case "equals":
            return answer.toLowerCase() === rule.sourceAnswerValue.toLowerCase();
        case "not_equals":
            return answer.toLowerCase() !== rule.sourceAnswerValue.toLowerCase();
        case "includes": {
            try {
                const parsed = JSON.parse(answer);
                if (Array.isArray(parsed)) {
                    const target = rule.sourceAnswerValue.toLowerCase();
                    return parsed.some((v) => String(v).toLowerCase() === target);
                }
            }
            catch {
                // not JSON — fall back to string equals
            }
            return answer.toLowerCase() === rule.sourceAnswerValue.toLowerCase();
        }
        case "greater_than": {
            const n = parseFloat(answer);
            const threshold = parseFloat(rule.sourceAnswerValue);
            return Number.isFinite(n) && Number.isFinite(threshold) && n > threshold;
        }
        case "less_than": {
            const n = parseFloat(answer);
            const threshold = parseFloat(rule.sourceAnswerValue);
            return Number.isFinite(n) && Number.isFinite(threshold) && n < threshold;
        }
        case "out_of_range": {
            const n = parseFloat(answer);
            if (!Number.isFinite(n)) {
                return false;
            }
            const [lowStr, highStr] = rule.sourceAnswerValue.split(",");
            const low = parseFloat(lowStr);
            const high = parseFloat(highStr);
            if (!Number.isFinite(low) || !Number.isFinite(high)) {
                return false;
            }
            return n < low || n > high;
        }
        case "any_of": {
            const candidates = rule.sourceAnswerValue
                .split(",")
                .map((c) => c.trim().toLowerCase())
                .filter((c) => c.length > 0);
            if (candidates.length === 0) {
                return false;
            }
            try {
                const parsed = JSON.parse(answer);
                if (Array.isArray(parsed)) {
                    return parsed.some((v) => candidates.includes(String(v).toLowerCase()));
                }
            }
            catch {
                // not JSON — fall through to single-value match
            }
            return candidates.includes(answer.toLowerCase());
        }
        default:
            return false;
    }
}
/**
 * Returns the set of visible question IDs given questions, their branching rules,
 * and current answers. A question is visible if it has no rules, OR all its rules
 * are satisfied (AND) AND its source question is also visible (cascading hide).
 */
export function evaluateVisibility(questions, answers) {
    const questionMap = new Map();
    for (const q of questions) {
        questionMap.set(q.id, q);
    }
    const cache = new Map();
    function isVisible(questionId) {
        const cached = cache.get(questionId);
        if (cached !== undefined) {
            return cached;
        }
        const question = questionMap.get(questionId);
        if (!question) {
            cache.set(questionId, false);
            return false;
        }
        if (question.branchingRules.length === 0) {
            cache.set(questionId, true);
            return true;
        }
        for (const rule of question.branchingRules) {
            if (!evaluateRule(rule, answers)) {
                cache.set(questionId, false);
                return false;
            }
            if (!isVisible(rule.sourceQuestionId)) {
                cache.set(questionId, false);
                return false;
            }
        }
        cache.set(questionId, true);
        return true;
    }
    const visible = new Set();
    for (const q of questions) {
        if (isVisible(q.id)) {
            visible.add(q.id);
        }
    }
    return visible;
}
/**
 * Compute the branching depth for each question. Depth 0 = no branching condition;
 * depth N = N levels deep in the condition chain.
 */
export function computeDepths(questions) {
    const questionMap = new Map();
    for (const q of questions) {
        questionMap.set(q.id, q);
    }
    const cache = new Map();
    function getDepth(questionId) {
        const cached = cache.get(questionId);
        if (cached !== undefined) {
            return cached;
        }
        const question = questionMap.get(questionId);
        if (!question || question.branchingRules.length === 0) {
            cache.set(questionId, 0);
            return 0;
        }
        let maxParentDepth = 0;
        for (const rule of question.branchingRules) {
            maxParentDepth = Math.max(maxParentDepth, getDepth(rule.sourceQuestionId));
        }
        const depth = maxParentDepth + 1;
        cache.set(questionId, depth);
        return depth;
    }
    const depths = new Map();
    for (const q of questions) {
        depths.set(q.id, getDepth(q.id));
    }
    return depths;
}
/**
 * Order branched questions under their trigger. Form data parks branched follow-ups
 * at high order_index values (≥1000 "append at end"), so ordering by order_index
 * alone drops them at the bottom of the section. This takes root questions already
 * in order_index order and re-emits ids so each branched question appears right
 * after the question that triggered it, depth-first. Sibling order is preserved.
 */
export function computeRenderOrder(questions) {
    const byId = new Map();
    for (const q of questions) {
        byId.set(q.id, q);
    }
    const childrenOf = new Map();
    const roots = [];
    for (const q of questions) {
        const parentId = q.branchingRules
            .map((r) => r.sourceQuestionId)
            .find((src) => src !== q.id && byId.has(src));
        if (parentId != null) {
            const list = childrenOf.get(parentId) ?? [];
            list.push(q);
            childrenOf.set(parentId, list);
        }
        else {
            roots.push(q);
        }
    }
    const result = [];
    const seen = new Set();
    function emit(q) {
        if (seen.has(q.id)) {
            return;
        }
        seen.add(q.id);
        result.push(q.id);
        for (const child of childrenOf.get(q.id) ?? []) {
            emit(child);
        }
    }
    for (const r of roots) {
        emit(r);
    }
    // Anything whose declared parent isn't in this set (cycles / cross-section refs)
    // is appended in input order so nothing disappears.
    for (const q of questions) {
        if (!seen.has(q.id)) {
            seen.add(q.id);
            result.push(q.id);
        }
    }
    return result;
}
//# sourceMappingURL=branching.js.map